import axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios'
import type { z } from 'zod'

import { env } from '@/config/env'

import { ApiError, parseApiResponse } from './envelope'
import { API } from './paths'
import { tokenPairSchema } from './schemas/auth'
import { azsErrorSchema, envelopeSchema } from './schemas/common'
import { tokenStorage } from './tokenStorage'

declare module 'axios' {
  export interface AxiosRequestConfig {
    /** Не подставлять Authorization и не пытаться обновить токен по 401. */
    skipAuth?: boolean
  }
}

type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean }

export const apiClient = axios.create({
  baseURL: env.apiUrl,
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json' },
})

/* ---------- реакция на потерю сессии ---------- */

let unauthorizedHandler: (() => void) | null = null

/** AuthProvider регистрирует обработчик, чтобы сбросить сессию в React-состоянии. */
export function setUnauthorizedHandler(handler: (() => void) | null) {
  unauthorizedHandler = handler
}

/* ---------- запрос: токен + X-Request-ID ---------- */

/** crypto.randomUUID доступен только в secure context (localhost/https); вне его — простой fallback. */
const requestId = (): string =>
  typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`

apiClient.interceptors.request.use((config) => {
  if (!config.skipAuth) {
    const token = tokenStorage.getAccess()
    if (token) config.headers.Authorization = `Bearer ${token}`
  }
  // CORS AZS API разрешает только Content-Type, поэтому свой заголовок шлём лишь на том же origin (через прокси).
  if (!env.apiUrl) config.headers['X-Request-ID'] = requestId()
  return config
})

/* ---------- ошибки: обёртка бэкенда важнее кода HTTP ---------- */

/**
 * Бэкенд на любой статус отдаёт { success:false, error:{ code, message } }.
 * Достаём это из тела ответа, чтобы интерфейс показывал «Событие не найдено»,
 * а не «Request failed with status code 404».
 */
function toApiError(error: AxiosError): ApiError {
  const status = error.response?.status
  const cause = { cause: error }
  // AZS API: { error: "Событие не найдено" }
  const azsError = azsErrorSchema.safeParse(error.response?.data)
  if (azsError.success) return new ApiError('API_ERROR', azsError.data.error, status, cause)
  // backend2.0: { success: false, error: { code, message } }
  const envelope = envelopeSchema.safeParse(error.response?.data)
  if (envelope.success && envelope.data.error) {
    return new ApiError(envelope.data.error.code, envelope.data.error.message, status, cause)
  }
  if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
    return new ApiError('TIMEOUT', 'Сервер не ответил вовремя', status, cause)
  }
  if (!error.response) return new ApiError('NETWORK', 'Нет соединения с сервером', undefined, cause)
  return new ApiError('HTTP_ERROR', `Ошибка сервера (${status})`, status, cause)
}

/* ---------- ответ: 401 → один refresh на все ожидающие запросы → повтор ---------- */

let refreshInFlight: Promise<string> | null = null

async function refreshAccessToken(): Promise<string> {
  const refreshToken = tokenStorage.getRefresh()
  if (!refreshToken) throw new ApiError('NO_REFRESH_TOKEN', 'Сессия истекла', 401)

  const response = await axios.post<unknown>(
    `${env.apiUrl}${API.auth.refresh}`,
    { refresh_token: refreshToken },
    { headers: { 'Content-Type': 'application/json' } },
  )
  const tokens = parseApiResponse(response.data, tokenPairSchema, response.status)
  tokenStorage.set(tokens)
  return tokens.access_token
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as RetriableConfig | undefined
    const status = error.response?.status

    if (status !== 401 || !config || config._retried || config.skipAuth || !env.authEnabled) {
      throw toApiError(error)
    }

    config._retried = true
    try {
      refreshInFlight ??= refreshAccessToken().finally(() => {
        refreshInFlight = null
      })
      const token = await refreshInFlight
      config.headers.Authorization = `Bearer ${token}`
      return apiClient(config)
    } catch (refreshError) {
      tokenStorage.clear()
      unauthorizedHandler?.()
      throw refreshError instanceof ApiError ? refreshError : toApiError(error)
    }
  },
)

/* ---------- типизированные обёртки: запрос + разбор обёртки + zod ---------- */

export async function getJson<T>(
  url: string,
  schema: z.ZodType<T>,
  config?: AxiosRequestConfig,
): Promise<T> {
  const response = await apiClient.get<unknown>(url, config)
  return parseApiResponse(response.data, schema, response.status)
}

export async function postJson<T>(
  url: string,
  body: unknown,
  schema: z.ZodType<T>,
  config?: AxiosRequestConfig,
): Promise<T> {
  const response = await apiClient.post<unknown>(url, body, config)
  return parseApiResponse(response.data, schema, response.status)
}
